import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { Button } from "react-aria-components";
import { twMerge } from "tailwind-merge";

interface CollapseProps {
  children: React.ReactNode;
  className?: string;
  defaultOpen?: boolean;
  disabled?: boolean;
  endAdorment?: JSX.Element;
  /** Controlled open state. Omit to let Collapse manage its own state (starting from `defaultOpen`). */
  isOpen?: boolean;
  label: JSX.Element;
  onOpenChange?: (isOpen: boolean) => void;
}

export function Collapse({
  children,
  className,
  defaultOpen = false,
  disabled,
  endAdorment,
  isOpen: controlledIsOpen,
  label,
  onOpenChange,
}: CollapseProps) {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(defaultOpen);
  const isOpen = controlledIsOpen ?? uncontrolledIsOpen;

  const toggle = () => {
    if (disabled) return;
    const next = !isOpen;

    if (controlledIsOpen === undefined) setUncontrolledIsOpen(next);
    onOpenChange?.(next);
  };

  return (
    <div className="w-full">
      <Button
        className={twMerge(
          "bg-bg-primary relative flex w-full items-center border border-border-primary",
          isOpen ? "rounded-t-md" : "rounded-md",
          className,
          "cursor-default text-md font-semibold text-button-secondary-fg",
          "outline-hidden transition",
          disabled ? "" : "cursor-pointer",
        )}
        onPress={toggle}
      >
        <span className="flex-1 px-3.5 py-2.5">{label}</span>
        <div
          className="flex items-center pr-3.5"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="mb-5 sm:mb-0">{endAdorment}</div>
        </div>
        <ChevronDown
          className={twMerge(
            "size-4 shrink-0 text-muted transition-transform mr-3.5",
            isOpen ? "rotate-180" : "",
            disabled ? "text-gray-400" : "",
          )}
        />
      </Button>
      <div
        aria-hidden={Boolean(!isOpen || disabled)}
        className={twMerge(
          "overflow-hidden transition-all duration-300 ease-in-out border border-t-0 border-border-primary rounded-b-md bg-bg-secondary",
          isOpen && !disabled ? "opacity-100 p-4" : "max-h-0 opacity-0 p-0",
        )}
      >
        {children}
      </div>
    </div>
  );
}
